import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MousePointerClick, FileText, RefreshCw, Calendar, Building2, Sparkles, AlertCircle, CheckCircle2, XCircle, Clock, Search, Globe, Megaphone } from "lucide-react";
import PageDateFilter from "@/components/PageDateFilter";

const FormSubmissionsPage = () => {
  const { sessionToken, isSuperAdmin } = useAuth();
  const { pageTitles } = useBranding();
  const { isPresentationMode, maskNumber, maskName, maskEmail, maskValue, maskSource } = usePresentationMode();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, icp_matches: 0, icp_no_match: 0, pending_evaluation: 0 });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [hubspotConnected, setHubspotConnected] = useState(true);
  const [evaluatingICP, setEvaluatingICP] = useState({});
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFilterInitialized, setDateFilterInitialized] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState(null);
  const [showIcpButton, setShowIcpButton] = useState(true);
  const [showIcpColumn, setShowIcpColumn] = useState(true);
  
  // Ref to track if fetch is in progress
  const fetchingRef = useRef(false);
  
  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // Get current tenant name for display
  const currentTenantName = sessionStorage.getItem("viewing_tenant_name") || "your organization";

  // Listen for tenant changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== FormSubmissionsPage: TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
      }
    };
    const interval = setInterval(checkTenantChange, 500);
    return () => clearInterval(interval);
  }, [currentTenantId]);

  // Fetch display settings to check if ICP button should be shown
  useEffect(() => {
    const fetchDisplaySettings = async () => {
      try {
        const viewingTenantId = currentTenantId;
        if (!viewingTenantId || !sessionToken) return;
        
        const response = await axios.get(`${API}/tenants/${viewingTenantId}/display-settings`, {
          params: { Authorization: `Bearer ${sessionToken}` }
        });
        setShowIcpButton(response.data?.show_icp_button !== false);
        setShowIcpColumn(response.data?.show_icp_column !== false);
      } catch (error) {
        console.error("Error fetching display settings:", error);
      }
    };
    fetchDisplaySettings();
  }, [sessionToken, currentTenantId]); // CRITICAL: Added currentTenantId

  const handleDateChange = useCallback((dateInfo) => {
    console.log("Form Submissions date filter changed:", dateInfo);
    setDateFilter(dateInfo);
    setDateFilterInitialized(true);
  }, []);

  const fetchSubmissions = useCallback(async () => {
    // Prevent concurrent fetches
    if (fetchingRef.current || !sessionToken) return;
    
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const viewingTenantId = currentTenantId;
      const params = { 
        Authorization: `Bearer ${sessionToken}`,
        limit: 500
      };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      if (dateFilter?.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter?.endDate) params.end_date = dateFilter.endDate;
      
      console.log("=== FormSubmissionsPage fetchSubmissions ===", "tenantId:", viewingTenantId);
      
      console.log("Fetching form submissions with params:", params);
      const response = await axios.get(`${API}/integrations/hubspot/form-submissions`, {
        params
      });
      
      let subs = response.data.submissions || [];
      
      // Filter by date range if specified (client-side backup filter)
      if (dateFilter?.startDate && dateFilter?.endDate) {
        const startMs = new Date(dateFilter.startDate).getTime();
        const endMs = new Date(dateFilter.endDate).getTime() + 86400000; // Include end date
        subs = subs.filter(s => {
          const subMs = s.submitted_at;
          return subMs >= startMs && subMs <= endMs;
        });
      }
      
      setSubmissions(subs);
      
      // Calculate stats
      const total = subs.length;
      const icp_matches = subs.filter(s => s.icp_status === 'match').length;
      const icp_no_match = subs.filter(s => s.icp_status === 'no_match').length;
      const pending_evaluation = total - icp_matches - icp_no_match;
      
      setStats({ total, icp_matches, icp_no_match, pending_evaluation });
      setHubspotConnected(true);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      
      // Set error message for display
      const errorMessage = error.response?.data?.detail || error.message || "Failed to fetch form submissions";
      setError(errorMessage);
      
      // Always show cached data on error
      setSubmissions([]);
      setStats({ total: 0, icp_matches: 0, icp_no_match: 0, pending_evaluation: 0 });
      if (error.response?.status === 400 || error.response?.status === 401) {
        setHubspotConnected(false);
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
      setInitialLoadDone(true);
    }
  }, [sessionToken, dateFilter?.startDate, dateFilter?.endDate, currentTenantId]); // CRITICAL: Added currentTenantId

  // Fetch when date filter changes
  useEffect(() => {
    if (dateFilterInitialized && sessionToken) {
      fetchSubmissions();
    }
  }, [dateFilterInitialized, dateFilter, sessionToken, fetchSubmissions, currentTenantId]); // CRITICAL: Added currentTenantId

  const syncFromHubSpot = async () => {
    setSyncing(true);
    try {
      const viewingTenantId = currentTenantId;
      const params = { 
        Authorization: `Bearer ${sessionToken}`,
        limit: 500
      };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      
      const response = await axios.get(`${API}/integrations/hubspot/form-submissions`, {
        params,
      });
      toast.success(`Synced ${response.data.total || response.data.submissions?.length || 0} form submissions from HubSpot`);
      // Refresh the local data
      await fetchSubmissions();
    } catch (error) {
      console.error("Error syncing from HubSpot:", error);
      if (error.response?.status === 400) {
        toast.error("Please connect HubSpot first in Admin > Integrations");
        setHubspotConnected(false);
      } else {
        toast.error(error.response?.data?.detail || "Failed to sync form submissions");
      }
    } finally {
      setSyncing(false);
    }
  };

  const getICPBadge = (status) => {
    if (!status || status === "pending") {
      return (
        <Badge className="bg-slate-100 text-slate-600 gap-1">
          <Clock className="w-3 h-3" />
          Pending
        </Badge>
      );
    }
    if (status === "match") {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 gap-1">
          <CheckCircle2 className="w-3 h-3" />
          ICP Match
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-700 gap-1">
        <XCircle className="w-3 h-3" />
        No Match
      </Badge>
    );
  };

  const getSourceBadge = (source) => {
    if (!source) return <span className="text-slate-400 text-xs">Unknown</span>;
    
    const sourceMap = {
      PAID_SEARCH: { label: "Paid Search", color: "bg-yellow-100 text-yellow-700" },
      ORGANIC_SEARCH: { label: "Organic", color: "bg-green-100 text-green-700" },
      DIRECT_TRAFFIC: { label: "Direct", color: "bg-blue-100 text-blue-700" },
      REFERRALS: { label: "Referral", color: "bg-purple-100 text-purple-700" },
      SOCIAL_MEDIA: { label: "Social", color: "bg-pink-100 text-pink-700" },
      EMAIL_MARKETING: { label: "Email", color: "bg-orange-100 text-orange-700" },
      PAID_SOCIAL: { label: "Paid Social", color: "bg-indigo-100 text-indigo-700" },
      OTHER_CAMPAIGNS: { label: "Campaign", color: "bg-teal-100 text-teal-700" },
      OFFLINE: { label: "Offline", color: "bg-slate-100 text-slate-600" },
    };
    
    const config = sourceMap[source] || { label: source, color: "bg-slate-100 text-slate-600" };
    return (
      <Badge className={`${config.color} text-xs`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="animate-fade-in" data-testid="form-submissions-page">
      {/* Page Header - Always visible */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{pageTitles?.form_submissions || "Form Submissions"}</h1>
          <p className="text-sm text-slate-500 mt-1">
            HubSpot form fills
          </p>
        </div>
        <div className="flex items-center gap-4">
          <PageDateFilter 
            pageId="form-submissions" 
            defaultPreset="last_30_days"
            onDateChange={handleDateChange}
          />
        </div>
      </div>

      {/* HubSpot Not Connected Warning */}
      {!hubspotConnected && submissions.length === 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">HubSpot Not Connected</p>
                <p className="text-sm text-amber-700">
                  Go to Admin &gt; Integrations to connect your HubSpot account and sync form submissions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-medium text-red-800">Error Loading Form Submissions</p>
                <p className="text-sm text-red-700">{error}</p>
                <p className="text-xs text-red-600 mt-1">Check browser console for more details.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {(loading || !dateFilterInitialized) && !initialLoadDone && (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Content - Show even while loading (for smoother UX) */}
      {(initialLoadDone || !loading) && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <Card className="kpi-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="kpi-label">Total Form Fills</p>
                    <p className="kpi-value">{maskNumber(stats.total)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-[#00A5A8]/10 flex items-center justify-center">
                    <MousePointerClick className="w-6 h-6 text-[#00A5A8]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="kpi-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="kpi-label">ICP Matches</p>
                    <p className="kpi-value text-emerald-600">{maskNumber(stats.icp_matches)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="kpi-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="kpi-label">Not ICP</p>
                    <p className="kpi-value text-red-600">{maskNumber(stats.icp_no_match)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="kpi-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="kpi-label">Pending Evaluation</p>
                    <p className="kpi-value text-slate-600">{maskNumber(stats.pending_evaluation)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Submissions Table */}
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#00A5A8]" />
                Form Submissions ({submissions.length})
                {loading && <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {submissions.length === 0 ? (
                <div className="text-center py-12">
                  <MousePointerClick className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No Form Submissions Yet</h3>
                  <p className="text-slate-500 mb-4">
                    {hubspotConnected 
                      ? "Form submissions will appear here once synced from HubSpot."
                      : "Connect HubSpot in Admin > Integrations to sync form submissions."}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contact</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Form</TableHead>
                      <TableHead>Submitted</TableHead>
                      {showIcpColumn && <TableHead>ICP Status</TableHead>}
                      {showIcpButton && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submissions.map((submission) => (
                      <TableRow key={submission.submission_id || submission.hubspot_submission_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {isPresentationMode 
                                ? maskName(`${submission.first_name || ""} ${submission.last_name || ""}`.trim())
                                : `${submission.first_name || "—"} ${submission.last_name || ""}`}
                            </p>
                            <p className="text-sm text-slate-500">
                              {isPresentationMode ? maskEmail(submission.email) : (submission.email || "No email")}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">
                              {isPresentationMode ? maskValue(submission.company, 'tenant') : (submission.company || "—")}
                            </span>
                          </div>
                          {submission.job_title && (
                            <p className="text-sm text-slate-500">
                              {isPresentationMode ? maskValue(submission.job_title) : submission.job_title}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {isPresentationMode ? (
                              <Badge className="bg-slate-100 text-slate-600 text-xs">Source ***</Badge>
                            ) : (
                              getSourceBadge(submission.analytics_source)
                            )}
                            {submission.analytics_source_data_1 && (
                              <span className="text-xs text-slate-400">
                                {isPresentationMode ? '***' : submission.analytics_source_data_1}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {isPresentationMode ? maskSource(submission.form_name, 'form') : (submission.form_name || "Unknown Form")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : "—"}
                          </span>
                          <span className="text-xs text-slate-400">
                            {submission.submitted_at ? new Date(submission.submitted_at).toLocaleTimeString() : ""}
                          </span>
                        </TableCell>
                        {showIcpColumn && <TableCell>{getICPBadge(submission.icp_status)}</TableCell>}
                        {showIcpButton && (
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs"
                              disabled={evaluatingICP[submission.submission_id]}
                              onClick={() => {
                                toast.info("ICP evaluation coming soon - configure ICP criteria in tenant settings first");
                              }}
                            >
                              {evaluatingICP[submission.submission_id] ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Search className="w-3 h-3 mr-1" />
                                  Assess ICP
                                </>
                              )}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {submissions.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Coming Soon:</strong> AI-powered ICP matching will automatically evaluate each form submission to determine if the company is an ICP fit for <strong>{currentTenantName}</strong>.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default FormSubmissionsPage;
